import { useEffect, useMemo, useRef, useState } from "react"

import { RESOURCES_NAMES } from "@/const/resource.constants"
import { TEASERS } from "@/const/teaser.constants"
import { isDataAvailable, noDataAvailable } from "@/helpers/context.helper"
import { scrollToElement } from "@/helpers/table.helpers"
import { useHttp } from "@/hooks/useHttp"
import { useInitialRender } from "@/hooks/useInitialRender"
import { useLogger } from "@/hooks/useLogger"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useOrderBy } from "@/hooks/useOrderBy"
import useServices from "@/hooks/useServices"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { ScanProgressEmptyTable } from "@/molecules/EmptyTable"
import { ExpandIconTable } from "@/molecules/ExpandIconTable"
import { TotalPagination } from "@/molecules/TotalPagination"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { LabelModal } from "@/organisms/LabelModal"
import {
  actions,
  useSelection
} from "@/pages/TriagePage/application/useSelection"
import {
  useFiltersTriage,
  useProjectsTriage
} from "@/pages/TriagePage/application/useTriage"
import { getFilters } from "@/pages/TriagePage/application/useTriage.selector"
import { TabTemplate } from "@/templates/TabTemplate"
import { IssueActions } from "../../molecules/IssueActions"
import { ExpandedRowTriageTab } from "../ExpandedRowTriageTab"
import { FilterTriageTab } from "../FilterTriageTab"
import { getColumnsTable } from "./getColumnsTable"

import type { Issue } from "@/domain/issue"

export const TriageTab = () => {
  const { t } = useTranslation()
  const { auth } = useServices()
  const exploitabilityEnabled = auth.isDemo()
  const featureFlags = useFeatureFlags()
  const internetFacingFF = featureFlags.internetFacingIntegration
  const convertors = useNumberConvertor()
  const { logger, EVENTS } = useLogger()
  const { onSorterChange, ...orderByProps } = useOrderBy({
    mixpanelEvent: EVENTS.ANALYTIC_EVENTS.TRIAGE.SORT_TABLE
  })
  const { onRowClick } = useDrawer("")
  const [record, setRecord] = useState<Issue | null>(null)
  const columns = getColumnsTable({
    t,
    convertors,
    orderByProps,
    onRowClick,
    internetFacingFF: !!internetFacingFF,
    isDemo: exploitabilityEnabled
  })

  const filterKeysRef = useRef<string[]>([])

  useEffect(() => {
    filterKeysRef.current = getFilters({
      t,
      internetFacingFF: !!internetFacingFF,
      exploitabilityEnabled
    }).map(({ key }) => key)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internetFacingFF, exploitabilityEnabled])

  const isEnabled = internetFacingFF !== undefined
  const [context, params] = useProjectsTriage(filterKeysRef.current, isEnabled)
  const { response, isLoading: projectsIsLoading } =
    useInitialRender<typeof context>(context)
  const [_, loadingFilters] = useFiltersTriage()
  const isLoading = projectsIsLoading || !isEnabled || loadingFilters

  const countContext = useHttp<{ data: number }>(
    `${RESOURCES_NAMES.TRIAGE.ISSUES_COUNT}`,
    params
  )
  const {
    isLoading: isCountLoading,
    response: countResponse = { data: undefined }
  } = countContext

  const { data, metadata } = response || {}
  const [selectionState, dispatch] = useSelection()
  const {
    selectedAll,
    selectedParentIds,
    unselectedIds,
    updating: updatingAll
  } = selectionState
  const noData = noDataAvailable(context, { isLoading })
  const isScmOrSensors =
    featureFlags.scmInstalled || featureFlags.kortexInstalled
  const showScanningProgress = isScmOrSensors && noData

  const selectedRowKeys = useMemo(
    () =>
      selectedAll
        ? (data || [])
            .map(({ key }) => key)
            .filter((key) => !unselectedIds.includes(key))
        : selectedParentIds,
    [selectedAll, unselectedIds, selectedParentIds, data]
  )

  useTooltip(data)

  return (
    <>
      <TabTemplate
        sticky
        loading={isLoading || !!updatingAll}
        resizable
        dataSource={data}
        columns={columns}
        metadata={metadata}
        locale={{
          customEmptyComponent: showScanningProgress && (
            <ScanProgressEmptyTable isLoading={isLoading} />
          ),
          teasers: {
            dataReadiness: true,
            kortexInstalled: TEASERS.SCM_INSTALLED.codeRepoNotConnected(t),
            scmInstalled: TEASERS.SCM_INSTALLED.codeRepoNotConnected(t)
          }
        }}
        rowSelection={{
          type: "checkbox",
          getCheckboxProps: (record) => {
            const currentRowState = selectionState.items[record.key] || {}
            const {
              nestedItems,
              updating,
              selectedState,
              selectedAllInProject,
              unselectedIds: unselectedIdsForCurrentRow
            } = currentRowState
            const disabled =
              !record.expandable ||
              nestedItems?.length === 0 ||
              updating ||
              updatingAll

            const isIndeterminate =
              (!selectedAll && selectedState === "indeterminate") ||
              (selectedAll && !!unselectedIdsForCurrentRow?.length)

            return {
              disabled,
              indeterminate: selectedAllInProject ? false : isIndeterminate
            }
          },
          selectedRowKeys,
          onSelect: (record, selected) => {
            if (selectedAll) {
              dispatch(actions.unselectRow(record.key, selected))

              return
            }

            const currentRowState = selectionState.items[record.key] || {}

            dispatch(
              actions.onSelectAllRowsInNestedTable(
                record.key,
                currentRowState?.selectedState === "indeterminate"
                  ? false
                  : selected
              )
            )
          },
          onChange: (_, __, { type }) => {
            if (type === "all") dispatch(actions.selectAll())
          }
        }}
        expandable={{
          expandIcon: ExpandIconTable,
          expandedRowRender: ({ key }, _index, _, expanded) => (
            <ExpandedRowTriageTab
              projectId={key}
              filterKeys={filterKeysRef.current}
              expanded={expanded}
              t={t}
              setRecord={setRecord}
              onCollapsed={() => {
                scrollToElement(key)

                dispatch(actions.expandedParentRow(key, false))
              }}
            />
          ),
          expandRowByClick: true,
          expandedRowKeys: selectionState.expandedRowKeys,
          rowExpandable: ({ expandable }) => !!expandable,
          onExpand: (expanded, record) => {
            if (expanded) {
              const resources = record?.resources || []
              const { id, name, type } = resources?.[0] || {}

              logger.info(EVENTS.ANALYTIC_EVENTS.TRIAGE.EXPAND_ROW, {
                id,
                name,
                type,
                relatedResources: resources.length - 1
              })
            }

            dispatch(actions.expandedParentRow(record.key, expanded))
          }
        }}
        onChange={(_pagination, _filters, sorter) => {
          onSorterChange(sorter)
        }}
        SearchComponent={
          <FilterTriageTab
            isDataAvailable={isDataAvailable(context)}
            searchedVulnerabilities={metadata?.searchedVulnerabilities}
          />
        }
        asideFooter={
          <IssueActions
            showSelectedProjects
            params={params}
            componentName="TriageTab"
            totalIssuesCount={countResponse.data}
            totalProjectsCount={metadata?.total}
          />
        }
        totalRender={({ loading, total }) => (
          <TotalPagination
            debounce={showScanningProgress ? 100 : undefined}
            totals={[
              {
                translationKey: "general.project",
                value: total
              },
              {
                translationKey: "general.issue",
                value: countResponse.data
              }
            ]}
            isLoading={loading || isCountLoading}
          />
        )}
      />
      {record && (
        <LabelModal
          placement="tooltip"
          component="triage-tab"
          record={record}
          onClose={() => setRecord(null)}
        />
      )}
    </>
  )
}
